import { type FormEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { ErrorNotice } from '../components/ui';

type Mode = 'password' | 'code';

/** Wireframe B2 — sign in by password or emailed code (FR-B2). */
export function SignIn() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [challengeRef, setChallengeRef] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (mode === 'password') {
        await api.signInWithPassword(email, password);
        navigate('/devices', { replace: true });
        return;
      }

      if (!challengeRef) {
        const result = await api.requestCode(email);
        setChallengeRef(result.challengeRef);
        return;
      }

      await api.signInWithCode(challengeRef, code);
      navigate('/devices', { replace: true });
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth">
      <div className="card">
        <div className="card-body">
          <h1>Sign in</h1>
          <p className="sub">NeteGate device management</p>

          <div className="tabs">
            <button
              type="button"
              className={mode === 'password' ? 'active' : ''}
              onClick={() => {
                setMode('password');
                setChallengeRef(null);
                setError(null);
              }}
            >
              Password
            </button>
            <button
              type="button"
              className={mode === 'code' ? 'active' : ''}
              onClick={() => {
                setMode('code');
                setError(null);
              }}
            >
              Email code
            </button>
          </div>

          <ErrorNotice error={error} />

          <form onSubmit={submit}>
            <div className="field">
              <label htmlFor="email">Email address</label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={Boolean(challengeRef)}
              />
            </div>

            {mode === 'password' ? (
              <div className="field">
                <label htmlFor="password">Password</label>
                <input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
            ) : null}

            {mode === 'code' && challengeRef ? (
              <div className="field">
                <label htmlFor="code">Six-digit code</label>
                <input
                  id="code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  required
                />
                <div className="hint">We have emailed a code to {email}. It expires in 10 minutes.</div>
              </div>
            ) : null}

            <button type="submit" disabled={busy}>
              {busy
                ? 'Please wait…'
                : mode === 'password'
                  ? 'Sign in'
                  : challengeRef
                    ? 'Verify code'
                    : 'Email me a code'}
            </button>
          </form>

          <div className="alt">
            New to NeteGate? <Link to="/signup">Create an account</Link>
          </div>
        </div>
      </div>
      <div className="qubow-footer muted" style={{ fontSize: '12px', textAlign: 'center', marginTop: '20px', width: '100%' }}>
        © 2026 Developed by <b><a href="https://www.qubow.com" target="_blank" aria-label="Qubow" style={{ color: 'inherit', textDecoration: 'none' }}>Qubow</a></b>
      </div>
    </div>
  );
}
